
import Vue from "vue";
import Papa from "papaparse";
import {
  uploadToS3,
  uploadToApi,
  getPhotoAnnounceActiveStatus,
  removePhotoAnnounce,
  parsePhotoAnnounceTargetIds,
  PhotoAnnounceParseResult
} from "./../../api";
import MasterPassDialog from "../../components/Dialogs/MasterPassDialog";
import { REGION_OPTIONS_GROUPED } from "../../utils/regions";
import {
  dropKnownHeaderRow,
  reasonLabel as reasonLabelUtil,
  formatBytes as formatBytesUtil
} from "../../utils/photoAnnounceIdList";

const ROLE_OPTIONS = [
  { value: 0, label: "Hygienist" },
  { value: 1, label: "Dental Assistant" }
];

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png"];

// T2-6085: targeted id-list upload (.xlsx / .csv). Pure parsing/formatting
// helpers live in utils/photoAnnounceIdList.ts so they can be unit-tested.
const MAX_ID_LIST_BYTES = 2 * 1024 * 1024;

export default Vue.extend({
  name: "FileUpload",
  components: { MasterPassDialog },
  data: () => {
    return {
      file: null as File | null,
      previewUrl: "" as string,
      fileError: "" as string,
      dragOver: false,
      loading: false,
      uploading: false,
      uploaded: false,
      uploadError: "" as string,
      uploadSuccessMessage: "" as string,
      url: "",
      announceUrl: null as string | null,
      expiresAt: "" as string,
      expiresMenu: false,
      previewDialog: false,
      masterPass: false,
      masterPassMode: "publish" as "publish" | "remove",
      target: "talent",
      selectedRoles: [] as number[],
      selectedProvinces: [] as string[],
      roleOptions: ROLE_OPTIONS,
      regionItems: REGION_OPTIONS_GROUPED,
      activeStatus: { talent: null as any, office: null as any },
      confirmRemove: false,
      removing: false,
      // T2-6085: targeted id-list state
      idListFile: null as File | null,
      idListError: "" as string,
      idListParsing: false,
      idListResult: null as PhotoAnnounceParseResult | null,
      idListInvalidDialog: false,
      idListClearedNotice: false,
      // T2-6085: monotonic token to fence stale async parse results (PR #435 review).
      idListParseToken: 0
    };
  },
  computed: {
    canPublish(): boolean {
      return (
        !!this.file &&
        !!this.expiresAt &&
        !this.fileError &&
        !this.idListBlocksPublish
      );
    },
    // T2-6085: an uploaded id list with no valid IDs (or still validating /
    // errored) blocks publish; no uploaded list does not block.
    idListBlocksPublish(): boolean {
      if (!this.idListFile) {
        return false;
      }
      if (this.idListParsing || this.idListError) {
        return true;
      }
      if (!this.idListResult || this.idListResult.validCount === 0) {
        return true;
      }
      // T2-6085: guard against a stale parse whose idType no longer matches the
      // currently-selected target (e.g. target switched after parse resolved).
      return this.idListResult.idType !== this.idListType;
    },
    idListType(): "hygienist" | "location" {
      return this.target === "office" ? "location" : "hygienist";
    },
    idListHelperCopy(): string {
      return this.target === "office"
        ? "Upload an Excel or CSV with one column of location IDs. When provided, the image will only show to those locations (combined with any province filters above)."
        : "Upload an Excel or CSV with one column of hygienist IDs. When provided, the image will only show to those hygienists (combined with any role / province filters above).";
    },
    minExpiresDate(): string {
      const today = new Date();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      return `${today.getFullYear()}-${month}-${day}`;
    },
    hasLiveImage(): boolean {
      return !!this.activeStatus[this.target as "talent" | "office"];
    },
    regionHint(): string {
      return this.target === "office"
        ? "Leave empty for all offices in any region."
        : "Leave empty for all regions.";
    },
    audienceSummaryHeadline(): string {
      const regions = this.selectedProvinces.join(", ");
      if (this.target === "office") {
        return regions ? `Office Accounts in ${regions}` : "All Office Accounts";
      }
      const roleLabels = this.selectedRoles
        .map((v: number) => ROLE_OPTIONS.find(r => r.value === v)?.label)
        .filter(Boolean)
        .join(", ");
      if (!roleLabels && !regions) {
        return "All Talent Accounts";
      }
      const roles = roleLabels || "Hygienists and Dental Assistants";
      return regions ? `${roles} in ${regions}` : roles;
    },
    audienceSummaryDetail(): string {
      if (this.target === "office") {
        return this.selectedProvinces.length === 0
          ? "This announcement will be shown to all Dental Office accounts."
          : "This announcement will be shown to offices in the selected regions.";
      }
      if (this.selectedRoles.length === 0 && this.selectedProvinces.length === 0) {
        return "No role or region filters selected. This announcement will be shown to all eligible Hygienists and Dental Assistants.";
      }
      return "Admins and Associate Dentists never receive in-app images.";
    }
  },
  watch: {
    target(newTarget: string, oldTarget: string) {
      if (newTarget !== "talent") {
        this.selectedRoles = [];
      }
      // T2-6085: switching audience invalidates the uploaded id list, since
      // hygienist ids and location ids are not interchangeable.
      if (newTarget !== oldTarget && this.idListFile) {
        this.clearIdList();
        this.idListClearedNotice = true;
      }
    }
  },
  methods: {
    triggerBrowse() {
      (this.$refs.fileInput as HTMLInputElement)?.click();
    },
    // T2-6192 QA: empty dropzone → browse; with a file selected, clicking the
    // zone/thumbnail opens the enlarged preview instead of re-opening browse.
    onDropzoneClick() {
      if (this.file) {
        this.previewDialog = true;
      } else {
        this.triggerBrowse();
      }
    },
    onFileSelected(event: Event) {
      const input = event.target as HTMLInputElement;
      const selected = input.files && input.files[0];
      if (selected) {
        this.setFile(selected);
      }
    },
    onDrop(event: DragEvent) {
      this.dragOver = false;
      const dropped = event.dataTransfer?.files && event.dataTransfer.files[0];
      if (dropped) {
        this.setFile(dropped);
      }
    },
    setFile(selected: File) {
      this.fileError = "";
      if (!ALLOWED_TYPES.includes(selected.type)) {
        this.fileError = "Invalid file type. Please upload a JPG or PNG image.";
        this.clearFile();
        return;
      }
      if (selected.size > MAX_FILE_BYTES) {
        this.fileError = "File is too large. Maximum size is 5 MB.";
        this.clearFile();
        return;
      }
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
      }
      this.file = selected;
      this.previewUrl = URL.createObjectURL(selected);
      this.uploadError = "";
    },
    clearFile() {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
      }
      this.file = null;
      this.previewUrl = "";
      const input = this.$refs.fileInput as HTMLInputElement;
      if (input) {
        input.value = "";
      }
    },
    clearUploadError() {
      this.uploadError = "";
    },
    // ── T2-6085: targeted id-list ─────────────────────────────────────────
    formatBytes(bytes: number): string {
      return formatBytesUtil(bytes);
    },
    reasonLabel(reason: string): string {
      return reasonLabelUtil(reason);
    },
    triggerIdListBrowse() {
      this.idListClearedNotice = false;
      (this.$refs.idListInput as HTMLInputElement)?.click();
    },
    onIdListSelected(event: Event) {
      const input = event.target as HTMLInputElement;
      const selected = input.files && input.files[0];
      if (selected) {
        this.parseIdListFile(selected);
      }
    },
    clearIdList() {
      // Invalidate any in-flight parse so a late resolution can't repopulate
      // a list the admin just cleared / switched away from (PR #435 review).
      this.idListParseToken += 1;
      this.idListFile = null;
      this.idListResult = null;
      this.idListError = "";
      this.idListParsing = false;
      const input = this.$refs.idListInput as HTMLInputElement;
      if (input) {
        input.value = "";
      }
    },
    removeIdList() {
      this.clearIdList();
      this.idListClearedNotice = false;
    },
    extractCsvFirstColumn(file: File): Promise<string[]> {
      return new Promise((resolve, reject) => {
        Papa.parse(file as any, {
          skipEmptyLines: true,
          complete: (res: { data: unknown[] }) => {
            const rows = (res.data as unknown[]).map((r: unknown) =>
              Array.isArray(r) ? r[0] : r
            );
            resolve(rows.map((v: unknown) => (v === null || v === undefined ? "" : String(v))));
          },
          error: (err: unknown) => reject(err)
        });
      });
    },
    async extractXlsxFirstColumn(file: File): Promise<string[]> {
      const XLSX = await import("xlsx");
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const firstSheet = wb.Sheets[wb.SheetNames[0]];
      const aoa = XLSX.utils.sheet_to_json(firstSheet, {
        header: 1,
        blankrows: false
      }) as unknown[][];
      const rows = aoa.map((r: unknown[]) => (Array.isArray(r) ? r[0] : r));
      return rows.map((v: unknown) => (v === null || v === undefined ? "" : String(v)));
    },
    async parseIdListFile(file: File) {
      this.idListError = "";
      this.idListResult = null;
      this.idListClearedNotice = false;
      if (file.size > MAX_ID_LIST_BYTES) {
        // clearIdList() resets idListError, so set the message AFTER clearing.
        this.clearIdList();
        this.idListError = "File is too large. Maximum size is 2 MB.";
        return;
      }
      const name = file.name.toLowerCase();
      const isCsv = name.endsWith(".csv");
      const isXlsx = name.endsWith(".xlsx");
      if (!isCsv && !isXlsx) {
        this.clearIdList();
        this.idListError = "Please upload a .xlsx or .csv file.";
        return;
      }
      this.idListFile = file;
      this.idListParsing = true;
      // Fence this run: a target switch / remove / new upload bumps the token and
      // makes this resolution stale, so it won't repopulate cleared state.
      const token = (this.idListParseToken += 1);
      const idTypeAtParse = this.idListType;
      try {
        const raw = isCsv
          ? await this.extractCsvFirstColumn(file)
          : await this.extractXlsxFirstColumn(file);
        const rows = dropKnownHeaderRow(raw);
        const result = await parsePhotoAnnounceTargetIds(idTypeAtParse, rows);
        if (token !== this.idListParseToken) {
          return; // stale — the form moved on while we were parsing
        }
        this.idListResult = result;
        if (result.validCount === 0) {
          this.idListError =
            "No valid IDs were found in this file. Please check the file and try again.";
        }
      } catch (e) {
        if (token !== this.idListParseToken) {
          return;
        }
        this.idListError =
          e instanceof Error ? e.message : "Could not read this file.";
        this.idListResult = null;
      } finally {
        if (token === this.idListParseToken) {
          this.idListParsing = false;
        }
      }
    },
    showSnackbar(text: string, color: string) {
      this.$store.commit("callSnackbar", {
        visible: true,
        text,
        color
      });
    },
    resetUploadFormValidation() {
      this.$nextTick(() => {
        const expiresAtField = this.$refs.expiresAtField as Vue & {
          resetValidation?: () => void;
        };
        if (expiresAtField && expiresAtField.resetValidation) {
          expiresAtField.resetValidation();
        }
      });
    },
    async refreshActiveStatus() {
      try {
        this.activeStatus = await getPhotoAnnounceActiveStatus();
      } catch (e) {
        /* non-fatal: button just won't show */
      }
    },
    openPublish() {
      this.uploadError = "";
      this.masterPassMode = "publish";
      this.masterPass = true;
    },
    onConfirmRemove() {
      this.confirmRemove = false;
      this.masterPassMode = "remove";
      this.masterPass = true;
    },
    validated(pass: string) {
      this.masterPass = false;
      if (this.masterPassMode === "remove") {
        this.doRemove(pass);
      } else {
        this.getFile();
      }
    },
    async doRemove(masterPass: string) {
      this.removing = true;
      try {
        await removePhotoAnnounce(this.target, masterPass);
        await this.refreshActiveStatus();
        this.showSnackbar("Removed.", "#13CA82");
        this.$emit("history-changed");
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Remove failed";
        this.showSnackbar(`Could not remove image: ${message}`, "#FB7672");
      } finally {
        this.removing = false;
      }
    },
    async getFile() {
      this.uploading = true;
      this.uploaded = false;
      this.uploadError = "";
      this.uploadSuccessMessage = "";
      try {
        this.url = await uploadToS3(
          this.file as File,
          this.file ? this.file.type : ""
        );
        const hasIdList =
          !!this.idListFile &&
          !!this.idListResult &&
          this.idListResult.validCount > 0 &&
          this.idListResult.idType === this.idListType;
        await uploadToApi(this.url, this.announceUrl, this.target, {
          roles: this.target === "talent" ? this.selectedRoles : [],
          provinces: this.selectedProvinces,
          expiresAt: this.expiresAt,
          targetIdListType: hasIdList ? this.idListType : null,
          targetIds:
            hasIdList && this.idListResult ? this.idListResult.validIds : []
        });
        this.uploaded = true;
        this.uploadSuccessMessage =
          "Photo announce saved and sent to the selected audience.";
        this.showSnackbar(this.uploadSuccessMessage, "#13CA82");
        this.refreshActiveStatus();
        this.$emit("history-changed");
        setTimeout(() => {
          this.uploaded = false;
          this.uploadSuccessMessage = "";
          this.clearFile();
          this.announceUrl = null;
          this.expiresAt = "";
          this.target = "talent";
          this.selectedRoles = [];
          this.selectedProvinces = [];
          this.clearIdList();
          this.idListClearedNotice = false;
          this.resetUploadFormValidation();
        }, 2000);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Upload failed";
        this.uploadError = message;
        this.showSnackbar(`Photo announce failed: ${message}`, "#FB7672");
      } finally {
        this.uploading = false;
      }
    }
  },
  created() {
    this.refreshActiveStatus();
  },
  beforeDestroy() {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
  }
});
